import { useState, useEffect, useCallback, useRef } from 'react'
import { proxyUrl } from './proxyBase'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WeatherCurrent {
  tempC: number
  feelsLikeC: number
  humidity: number
  windKph: number
  windDir: string
  precipMm: number
  weatherCode: number
  isDay: boolean
}

export interface WeatherDaily {
  date: string
  maxC: number
  minC: number
  precipMm: number
  weatherCode: number
  uvIndex: number
  sunrise: string
  sunset: string
}

export interface LocationWeather {
  label: string
  lat: number
  lng: number
  current: WeatherCurrent
  daily: WeatherDaily[]
}

export interface UseWeatherResult {
  locations: LocationWeather[]
  loading: boolean
  error: string | null
  refresh: () => void
}

// ── WMO weather code → label + icon ──────────────────────────────────────────

export function wmoToMeta(code: number, isDay = true): { label: string; icon: string; color: string } {
  if (code === 0)            return { label: 'Clear',          icon: isDay ? 'fa-sun' : 'fa-moon',          color: 'text-yellow-500' }
  if (code <= 2)             return { label: 'Partly cloudy',  icon: 'fa-cloud-sun',                        color: 'text-yellow-400' }
  if (code === 3)            return { label: 'Overcast',       icon: 'fa-cloud',                            color: 'text-slate-400'   }
  if (code <= 49)            return { label: 'Foggy',          icon: 'fa-smog',                             color: 'text-slate-400'   }
  if (code <= 59)            return { label: 'Drizzle',        icon: 'fa-cloud-drizzle',                    color: 'text-blue-400'   }
  if (code <= 69)            return { label: 'Rain',           icon: 'fa-cloud-rain',                       color: 'text-blue-500'   }
  if (code <= 79)            return { label: 'Snow',           icon: 'fa-snowflake',                        color: 'text-blue-200'   }
  if (code <= 84)            return { label: 'Rain showers',   icon: 'fa-cloud-showers-heavy',              color: 'text-blue-500'   }
  if (code <= 94)            return { label: 'Thunderstorm',   icon: 'fa-cloud-bolt',                       color: 'text-warning-500'}
  return                            { label: 'Thunderstorm',   icon: 'fa-cloud-bolt',                       color: 'text-warning-600'}
}

export function windDirLabel(deg: number): string {
  const dirs = ['N','NE','E','SE','S','SW','W','NW']
  return dirs[Math.round(deg / 45) % 8]
}

// ── Cache ─────────────────────────────────────────────────────────────────────

const WEATHER_TTL_MS = 60 * 60 * 1000  // 1 hour
const weatherCache = new Map<string, { data: LocationWeather; ts: number }>()

function weatherCacheKey(lat: number, lng: number): string {
  return `${Math.round(lat * 100)}:${Math.round(lng * 100)}`
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchLocationWeather(
  label: string,
  lat: number,
  lng: number,
  signal?: AbortSignal,
  force = false,
): Promise<LocationWeather> {
  const key = weatherCacheKey(lat, lng)
  if (!force) {
    const hit = weatherCache.get(key)
    if (hit && Date.now() - hit.ts < WEATHER_TTL_MS) return { ...hit.data, label }
  }

  const params = new URLSearchParams({
    lat: lat.toString(),
    lng: lng.toString(),
  })

  const res = await fetch(
    `${proxyUrl('weatherProxy')}?${params}`,
    { signal },
  )
  if (!res.ok) throw new Error(`Open-Meteo error ${res.status}`)
  const d = await res.json()

  const c = d.current
  const daily: WeatherDaily[] = (d.daily.time as string[]).map((date: string, i: number) => ({
    date,
    maxC: d.daily.temperature_2m_max[i],
    minC: d.daily.temperature_2m_min[i],
    precipMm: d.daily.precipitation_sum[i],
    weatherCode: d.daily.weather_code[i],
    uvIndex: d.daily.uv_index_max[i],
    sunrise: d.daily.sunrise[i] as string,
    sunset: d.daily.sunset[i] as string,
  }))

  const result: LocationWeather = {
    label,
    lat,
    lng,
    current: {
      tempC: c.temperature_2m,
      feelsLikeC: c.apparent_temperature,
      humidity: c.relative_humidity_2m,
      windKph: c.wind_speed_10m,
      windDir: windDirLabel(c.wind_direction_10m),
      precipMm: c.precipitation,
      weatherCode: c.weather_code,
      isDay: c.is_day === 1,
    },
    daily,
  }
  weatherCache.set(key, { data: result, ts: Date.now() })
  return result
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface WeatherLocation {
  label: string
  lat: number
  lng: number
}

export function useWeather(locations: WeatherLocation[]): UseWeatherResult {
  const [results, setResults] = useState<LocationWeather[]>(() =>
    locations.flatMap(l => {
      const hit = weatherCache.get(weatherCacheKey(l.lat, l.lng))
      return hit && Date.now() - hit.ts < WEATHER_TTL_MS ? [{ ...hit.data, label: l.label }] : []
    })
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const forceRef = useRef(false)

  const refresh = useCallback(() => { forceRef.current = true; setTick(t => t + 1) }, [])

  useEffect(() => {
    if (locations.length === 0) return
    const ctrl = new AbortController()
    const force = forceRef.current
    forceRef.current = false
    setLoading(true)
    setError(null)

    const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
      Promise.race([p, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))])

    Promise.allSettled(
      locations.map(l => withTimeout(fetchLocationWeather(l.label, l.lat, l.lng, ctrl.signal, force), 10_000))
    ).then(settled => {
      if (ctrl.signal.aborted) return
      const successes = settled
        .filter((r): r is PromiseFulfilledResult<LocationWeather> => r.status === 'fulfilled')
        .map(r => r.value)
      setResults(successes)
      if (successes.length === 0) setError('Unable to load weather data')
      setLoading(false)
    })

    return () => ctrl.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations.map(l => `${l.lat},${l.lng}`).join('|'), tick])

  return { locations: results, loading, error, refresh }
}
